import { useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Contact.css";

const STUDIO = {
  name: "Lash With Brittany",
  street: "30 Erie Canal Dr Suite E",
  city: "Rochester",
  region: "NY",
  postal: "14626",
  country: "US",
  phone: "+15853312162",
  phonePretty: "(585) 331 2162",
  email: "Lashwithbrittany@gmail.com",
  site: "https://lashwithbrittany.com",
  path: "/contact",
  directionsUrl:
    "https://www.google.com/maps/dir/?api=1&destination=30+Erie+Canal+Dr+Suite+E%2C+Rochester%2C+NY+14626",
  mapEmbedSrc:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2909.10654141307!2d-77.70588552336552!3d43.18627528250426!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d6b16c4f3c16b1%3A0x93cc09ab44bcaba0!2sLash%20With%20Brittany!5e0!3m2!1sen!2sus!4v1728133674449!5m2!1sen!2sus",
};

export default function Contact() {
  const formRef = useRef(null);
  const [isSending, setIsSending] = useState(false);

  const faqs = useMemo(
    () => [
      {
        q: "Where is the studio located",
        a: (
          <>
            We’re inside <strong>{STUDIO.street}</strong>, {STUDIO.city},{" "}
            {STUDIO.region} {STUDIO.postal}. You can view the map below or open
            directions in Google Maps.
          </>
        ),
      },
      {
        q: "How should I contact you",
        a: (
          <>
            The fastest way is by phone at{" "}
            <a href={`tel:${STUDIO.phone}`}>{STUDIO.phonePretty}</a>. You can
            also email{" "}
            <a href={`mailto:${STUDIO.email}`}>{STUDIO.email}</a> or send a
            message using the form.
          </>
        ),
      },
      {
        q: "Do I need to book in advance",
        a: (
          <>
            Yes. Appointments are required. Booking one to two weeks ahead is a
            good rule for evenings and weekends.
          </>
        ),
      },
      {
        q: "What if I have extensions from another salon",
        a: (
          <>
            We can usually work with existing extensions, but every set is
            different. At your first visit we will assess your lashes and
            recommend the best option, including a removal and fresh full set if
            needed.
          </>
        ),
      },
      {
        q: "How do I prepare for my appointment",
        a: (
          <>
            Arrive with clean lashes free of makeup and oils. Avoid caffeine
            before your visit so you can fully relax during your service.
          </>
        ),
      },
      {
        q: "What is your cancellation policy",
        a: (
          <>
            Please provide at least 24 hours notice if you need to cancel or
            reschedule. Same day cancellations and no shows may be subject to a
            fee. Full details are on the Policies page.
          </>
        ),
      },
    ],
    []
  );

  const businessJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BeautySalon",
      name: STUDIO.name,
      url: STUDIO.site + STUDIO.path,
      email: STUDIO.email,
      telephone: STUDIO.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: STUDIO.street,
        addressLocality: STUDIO.city,
        addressRegion: STUDIO.region,
        postalCode: STUDIO.postal,
        addressCountry: STUDIO.country,
      },
    }),
    []
  );

  const breadcrumbJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: STUDIO.site },
        {
          "@type": "ListItem",
          position: 2,
          name: "Contact",
          item: STUDIO.site + STUDIO.path,
        },
      ],
    }),
    []
  );

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text:
            typeof f.a === "string"
              ? f.a
              : "See the Contact page for the full answer.",
        },
      })),
    }),
    [faqs]
  );

  const sendEmail = (e) => {
    e.preventDefault();
    if (isSending) return;

    const formEl = formRef.current;
    if (!formEl) return;

    const name = formEl.name.value.trim();
    const email = formEl.email.value.trim();
    const phone = formEl.phone.value.trim();
    const message = formEl.message.value.trim();

    if (!name || !email || !message) {
      toast.error("Please fill in Name, Email, and Message.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (phone && !/^[0-9+\s()]+$/.test(phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !userId) {
      toast.error("Email is not configured yet. Please call or email directly.");
      return;
    }

    setIsSending(true);

    emailjs
      .sendForm(serviceId, templateId, formEl, userId)
      .then(() => {
        toast.success("Message sent successfully.");
        formEl.reset();
      })
      .catch(() => {
        toast.error("Failed to send the message. Please try again.");
      })
      .finally(() => setIsSending(false));
  };

  return (
    <main className="contact-page">
      <Helmet>
        <title>Contact Lash With Brittany in Rochester NY</title>
        <meta
          name="description"
          content="Call, email, get directions, or send a message to Lash With Brittany in Rochester NY. Book online in minutes."
        />
        <link rel="canonical" href={STUDIO.site + STUDIO.path} />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Contact Lash With Brittany in Rochester NY"
        />
        <meta
          property="og:description"
          content="Reach the studio by phone or email, get directions, or send a quick message."
        />
        <meta property="og:url" content={STUDIO.site + STUDIO.path} />
        <meta name="twitter:card" content="summary" />

        <script type="application/ld+json">{JSON.stringify(businessJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <section className="contact-hero">
        <div className="contact-shell">
        <div className="contact-hero-border">
          <div className="contact-hero-card">
            <p className="contact-eyebrow">Contact</p>
            <h1 className="contact-title">Contact Lash With Brittany in Rochester NY</h1>

            <p className="contact-lead">
              Call, email, get directions, or send a message. If you are ready to
              book, the fastest option is online booking.
            </p>

            <div className="contact-actions">
              <a className="contact-cta contact-cta-primary" href="/book">
                Book Now
              </a>
              <a
                className="contact-cta contact-cta-secondary"
                href={`tel:${STUDIO.phone}`}
              >
                Call {STUDIO.phonePretty}
              </a>
              <a
                className="contact-cta contact-cta-secondary"
                href={`mailto:${STUDIO.email}`}
              >
                Email
              </a>
              <a
                className="contact-cta contact-cta-secondary"
                href={STUDIO.directionsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Directions
              </a>
            </div>
          </div>
        </div>
        </div>
      </section>

      <section className="contact-grid contact-shell">
        <article className="contact-card contact-card-info">
          <header className="contact-card-header">
            <h2>Studio Details</h2>
            <p>Everything you need to find us and reach us quickly.</p>
          </header>

          <div className="contact-rows">
            <div className="contact-row">
              <div className="contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11z"
                    fill="currentColor"
                    opacity="0.9"
                  />
                  <circle cx="12" cy="10" r="2.6" fill="#fff" />
                </svg>
              </div>
              <div className="contact-row-body">
                <div className="contact-row-title">Address</div>
                <div className="contact-row-text">
                  {STUDIO.street}
                  <br />
                  {STUDIO.city}, {STUDIO.region} {STUDIO.postal}
                </div>
              </div>
            </div>

            <div className="contact-row">
              <div className="contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M6.6 10.8c1.6 3 3.6 5 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.4 21 3 13.6 3 4c0-.6.4-1 1-1h2.8c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.4 0 .8-.2 1.1l-2.2 2.2z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div className="contact-row-body">
                <div className="contact-row-title">Phone</div>
                <div className="contact-row-text">
                  <a href={`tel:${STUDIO.phone}`}>{STUDIO.phonePretty}</a>
                </div>
              </div>
            </div>

            <div className="contact-row">
              <div className="contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M20 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"
                    fill="currentColor"
                    opacity="0.9"
                  />
                  <path
                    d="M4 7l8 6 8-6"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="contact-row-body">
                <div className="contact-row-title">Email</div>
                <div className="contact-row-text">
                  <a href={`mailto:${STUDIO.email}`}>{STUDIO.email}</a>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-chips">
            <a className="contact-chip" href="/pricelist">Services</a>
            <a className="contact-chip" href="/book">Booking</a>
            <a className="contact-chip" href="/giftcards">Gift Cards</a>
            <a className="contact-chip" href="/policy">Policies</a>
            <a className="contact-chip" href="/reviews">Reviews</a>
            <a className="contact-chip" href="/permanent-jewelry">Permanent Jewelry</a>
          </div>
        </article>

        <aside id="map" className="contact-card contact-card-map" aria-label="Map">
          <header className="contact-card-header">
            <h2>Map and Directions</h2>
            <p>Tap directions to open your navigation app.</p>
          </header>

          <div className="contact-map-frame">
            <iframe
              title="Lash With Brittany Map"
              src={STUDIO.mapEmbedSrc}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <p className="contact-map-note">
            Prefer your own app?{" "}
            <a href={STUDIO.directionsUrl} target="_blank" rel="noreferrer">
              Open directions in Google Maps
            </a>
            .
          </p>
        </aside>
      </section>

      <section className="contact-lower contact-shell">
        <div className="contact-toast-scope">
        <section className="contact-card contact-card-form">
          <header className="contact-card-header">
            <h2>Send a Message</h2>
            <p>
              For fastest booking, use the Book Now button above. For questions,
              send a message here.
            </p>
          </header>

          <form
            ref={formRef}
            onSubmit={sendEmail}
            className="contact-form"
          >
            <div className="contact-form-grid">
              <label className="contact-field">
                <span className="contact-label">
                  Name <span className="contact-required" aria-hidden="true">*</span>
                </span>
                <input
                  className="contact-input"
                  type="text"
                  name="name"
                  autoComplete="name"
                  required
                />
              </label>

              <label className="contact-field">
                <span className="contact-label">
                  Email <span className="contact-required" aria-hidden="true">*</span>
                </span>
                <input
                  className="contact-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </label>

              <label className="contact-field">
                <span className="contact-label">Phone</span>
                <input
                  className="contact-input"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </label>

              <label className="contact-field contact-field-full">
                <span className="contact-label">
                  Message <span className="contact-required" aria-hidden="true">*</span>
                </span>
                <textarea
                  className="contact-input contact-textarea"
                  name="message"
                  rows="6"
                  required
                />
              </label>
            </div>

            <div className="contact-form-actions">
              <button
                type="submit"
                className="contact-cta contact-cta-primary contact-submit"
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Send Message"}
              </button>

              <a className="contact-cta contact-cta-secondary" href="/book">
                Book Instead
              </a>
            </div>

            <p className="contact-form-note">
              Required fields are Name, Email, and Message.
            </p>
          </form>

          
        </section>
            <ToastContainer position="bottom-right" autoClose={4000} newestOnTop />
        </div>

        <section className="contact-card contact-card-faq">
          <header className="contact-card-header">
            <h2>Quick Answers</h2>
            <p>Tap a question to expand. Policies are available on the Policies page.</p>
          </header>

          <div className="contact-faq">
            {faqs.map((item) => (
              <details key={item.q} className="contact-faq-item">
                <summary className="contact-faq-q">{item.q}</summary>
                <div className="contact-faq-a">
                  <p>{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}
